// doctor-frontend/src/components/profile/ProfileCard.jsx
import React from 'react';

function deepGet(obj, path) {
  return String(path)
    .split('.')
    .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

function firstOf(obj, paths = []) {
  for (const p of paths) {
    const val = p.includes('.') ? deepGet(obj, p) : obj?.[p];
    if (val != null && val !== '') return val;
  }
  return '';
}

export default function ProfileCard({ profile }) {
  if (!profile) return null;

  // Tolerate multiple historical shapes for goals
  const goalsShort = firstOf(profile, [
    'goalsShortTerm', 'shortTermGoal', 'goals.shortTerm', 'short_goal', 'shortterm', 'shortTerm'
  ]);
  const goalsLong = firstOf(profile, [
    'goalsLongTerm', 'longTermGoal', 'goals.longTerm', 'long_goal', 'longterm', 'longTerm'
  ]);

  const {
    displayName,
    designation,
    departments = [],
    centres = [],
    qualifications = [],
    registrationNumber,
    experienceYears,
    photoURL,
    signatureURL,
    emailAlt,
    mobileAlt,
    officeLocation,
    workingHours,
    areasOfInterest = [],
    languagePrefs = [],
    dashboardLayout,
    bio,
    mentorUserId,
    superviseeUserIds = [],
  } = profile;

  // Normalize areas/languages for display
  const areasList = Array.isArray(areasOfInterest)
    ? areasOfInterest
    : String(areasOfInterest || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

  const langsList = Array.isArray(languagePrefs)
    ? languagePrefs
    : String(languagePrefs || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

  // Compact KPI ribbon (derived; no API calls; no regression)
  const kpis = [
    { label: 'Experience', value: (experienceYears ?? null) !== null ? `${Number(experienceYears) || 0} yrs` : '—' },
    { label: 'Centres', value: Array.isArray(centres) ? centres.length : (centres ? 1 : 0) },
    { label: 'Languages', value: langsList.length || '—' },
    { label: 'Supervisees', value: Array.isArray(superviseeUserIds) ? superviseeUserIds.length : 0 },
  ];


  return (
    <div className="rounded-2xl shadow-md p-5 bg-white dark:bg-neutral-900 dark:text-neutral-100 border border-neutral-100 dark:border-neutral-800">
      {/* Header */}
      <div className="flex items-center gap-4">
        <img
          src={
            photoURL ||
            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNzIiIGhlaWdodD0iNzIiIHZpZXdCb3g9IjAgMCA3MiA3MiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCBmaWxsPSIjZWRlZGVkIiB3aWR0aD0iNzIiIGhlaWdodD0iNzIiIHJ4PSIzNiIvPjxwYXRoIGQ9Ik0zNiAyM0MzMS4wMjkgMjMgMjcgMjcuMDI5IDI3IDMyQzI3IDM2Ljk3MSAzMS4wMjkgNDEgMzYgNDFDNDAuOTcxIDQxIDQ1IDM2Ljk3MSA0NSAzMkM0NSAyNy4wMjkgNDAuOTcxIDIzIDM2IDIzWk0zNiA0NEMyNS40OTUgNDQgMTcgNTEuNDk1IDE3IDYySDU1QzU1IDUxLjQ5NSA0Ni41MDUgNDQgMzYgNDRaIiBmaWxsPSIjY2NjIi8+PC9zdmc+'
          }
          alt="Profile"
          className="w-20 h-20 object-cover rounded-full border border-neutral-300 dark:border-neutral-700"
        />
        <div className="flex-1">
          <h2 className="text-xl font-semibold">{displayName || '—'}</h2>
          <p className="text-sm opacity-80">
            {designation || '—'}
            {departments?.length ? ` • ${departments.join(', ')}` : ''}
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {profile?.role ? (
              <span className="text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-900 border border-purple-200 dark:bg-purple-900/20 dark:text-purple-100 dark:border-purple-800">
                {String(profile.role)}
              </span>
            ) : null}
            {Array.isArray(centres) && centres.length ? (
              <span className="text-xs px-2 py-1 rounded-full bg-orange-50 text-orange-900 border border-orange-200 dark:bg-orange-900/20 dark:text-orange-100 dark:border-orange-800">
                {centres.length === 1 ? centres[0] : `${centres.length} centres`}
              </span>
            ) : null}
            {registrationNumber ? (
              <span className="text-xs px-2 py-1 rounded-full bg-neutral-50 text-neutral-900 border border-neutral-200 dark:bg-neutral-800 dark:text-neutral-100 dark:border-neutral-700">
                Reg: {registrationNumber}
              </span>
            ) : null}
          </div>

          {!!centres?.length && (
            <p className="text-xs opacity-70 mt-1">Centres: {centres.join(', ')}</p>
          )}
        </div>
      </div>

      {/* Quick facts */}
      <div className="mt-5 mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold opacity-80">Quick facts</div>
        <div className="text-xs opacity-60">Profile summary</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        {!!qualifications?.length && (
          <div>
            <div className="opacity-60">Qualifications</div>
            <div>{qualifications.join(', ')}</div>
          </div>
        )}
        {registrationNumber && (
          <div>
            <div className="opacity-60">Registration No.</div>
            <div>{registrationNumber}</div>
          </div>
        )}
        {(experienceYears ?? null) !== null && (
          <div>
            <div className="opacity-60">Experience</div>
            <div>{Number(experienceYears) || 0} years</div>
          </div>
        )}
        <div>
          <div className="opacity-60">Alt Email</div>
          <div>{emailAlt || '—'}</div>
        </div>
        <div>
          <div className="opacity-60">Alt Mobile</div>
          <div>{mobileAlt || '—'}</div>
        </div>
        <div>
          <div className="opacity-60">Office</div>
          <div>{officeLocation || '—'}</div>
        </div>
        <div>
          <div className="opacity-60">Hours</div>
          <div>{workingHours || '—'}</div>
        </div>

        {!!areasList.length && (
          <div className="md:col-span-2">
            <div className="opacity-60">Areas of Interest</div>
            <div>{areasList.join(', ')}</div>
          </div>
        )}
        {!!langsList.length && (
          <div>
            <div className="opacity-60">Languages</div>
            <div>{langsList.join(', ')}</div>
          </div>
        )}
        {dashboardLayout && (
          <div>
            <div className="opacity-60">Dashboard Layout</div>
            <div>{dashboardLayout}</div>
          </div>
        )}
        {(mentorUserId || superviseeUserIds.length) ? (
          <>
            {mentorUserId && (
              <div>
                <div className="opacity-60">Mentor User ID</div>
                <div className="break-all">{mentorUserId}</div>
              </div>
            )}
            {!!superviseeUserIds.length && (
              <div className="md:col-span-2">
                <div className="opacity-60">Supervisees</div>
                <div className="break-words">
                  {superviseeUserIds.join(', ')}
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Bio */}
      {bio && (
        <div className="mt-4">
          <div className="opacity-60 mb-1">Bio</div>
          <div className="whitespace-pre-wrap text-sm">{bio}</div>
        </div>
      )}

      {/* Goals (supports many historical key shapes) */}
      {(goalsShort || goalsLong) && (
        <div className="mt-4">
          <div className="opacity-60 mb-1">Goals</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {goalsShort && (
              <div>
                <div className="text-xs opacity-70">Short-term</div>
                <div className="whitespace-pre-wrap text-sm">{goalsShort}</div>
              </div>
            )}
            {goalsLong && (
              <div>
                <div className="text-xs opacity-70">Long-term</div>
                <div className="whitespace-pre-wrap text-sm">{goalsLong}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Signature (optional) */}
      {signatureURL && (
        <div className="mt-4">
          <div className="opacity-60 mb-1">Signature</div>
          <img src={signatureURL} alt="Signature" className="max-h-16 object-contain" />
        </div>
      )}
    </div>
  );
}